// src/components/Settings.tsx
import { useState, ChangeEvent } from 'react';
import {
	useMantineColorScheme,
	Stack,
	Text,
	Paper,
	Switch,
	Button,
	Group,
	Tooltip,
	Avatar,
} from '@mantine/core';
import {
	IconBrandGoogle,
	IconInfoCircle,
	IconCloud,
	IconDownload,
	IconRefresh,
} from '@tabler/icons-react';
import Backup from './Backup';
import AuthModal from './AuthModal';
import { useCloudBackup } from '../hooks/useCloudBackup';
import { Bill } from '../types';

interface SettingsProps {
	bills: Bill[];
	onRestoreBills: (bills: Bill[]) => void;
	onCheckForUpdates: () => Promise<void>;
	isCheckingUpdates: boolean;
	appVersion: string;
}

function Settings({
	bills,
	onRestoreBills,
	onCheckForUpdates,
	isCheckingUpdates,
	appVersion,
}: SettingsProps) {
	const [showAuthModal, setShowAuthModal] = useState(false);
	const { colorScheme, setColorScheme } = useMantineColorScheme();
	const [showPercentages, setShowPercentages] = useState(() => {
		return JSON.parse(
			localStorage.getItem('show-bill-percentages') || 'false'
		);
	});

	const {
		loading: cloudLoading,
		signInWithGoogle,
		signOutUser,
		saveToCloud,
		restoreFromCloud,
		user,
		lastBackupDate,
	} = useCloudBackup();

	const handlePercentagesToggle = (event: ChangeEvent<HTMLInputElement>) => {
		const newValue = event.currentTarget.checked;
		setShowPercentages(newValue);
		localStorage.setItem('show-bill-percentages', JSON.stringify(newValue));
	};

	const handleThemeToggle = (event: ChangeEvent<HTMLInputElement>) => {
		const newTheme = event.currentTarget.checked ? 'dark' : 'light';
		setColorScheme(newTheme);
	};

	const handleSignInClick = async () => {
		try {
			await signInWithGoogle();
			setShowAuthModal(false);
		} catch (error) {
			console.error('Sign in failed:', error);
		}
	};

	const handleCloudBackup = async () => {
		try {
			await saveToCloud(bills);
		} catch (error) {
			console.error('Cloud backup failed:', error);
		}
	};

	const handleCloudRestore = async () => {
		try {
			const restoredBills = await restoreFromCloud();
			onRestoreBills(restoredBills);
		} catch (error) {
			console.error('Cloud restore failed:', error);
		}
	};

	return (
		<Stack gap='xl'>
			{/* Cloud Backup Section */}
			<Paper withBorder p='md' radius='md'>
				<Stack gap='md'>
					<Group justify='apart' align='center'>
						<Text size='sm' fw={500}>
							Cloud Backup
						</Text>
						<Tooltip
							label='Cloud backup keeps your data safe and synced across devices'
							position='top'
							withArrow
						>
							<IconInfoCircle
								size={18}
								style={{ cursor: 'pointer' }}
							/>
						</Tooltip>
					</Group>

					{user ? (
						<>
							<Group>
								<Avatar
									src={user.photoURL}
									radius='xl'
									size='sm'
								/>
								<Text size='sm'>{user.email}</Text>
							</Group>
							{lastBackupDate && (
								<Text size='sm' c='dimmed'>
									Last backup:{' '}
									{lastBackupDate.toLocaleString('en-US', {
										month: 'short',
										day: 'numeric',
										year: 'numeric',
										hour: 'numeric',
										minute: 'numeric',
										hour12: true,
									})}
								</Text>
							)}
							<Group>
								<Button
									leftSection={<IconCloud size={16} />}
									onClick={handleCloudBackup}
									loading={cloudLoading}
									variant='filled'
								>
									Cloud Backup
								</Button>
								<Button
									leftSection={<IconDownload size={16} />}
									onClick={handleCloudRestore}
									loading={cloudLoading}
									variant='light'
								>
									Cloud Restore
								</Button>
								<Button
									onClick={signOutUser}
									variant='light'
									color='gray'
								>
									Sign Out
								</Button>
							</Group>
						</>
					) : (
						<Button
							leftSection={<IconBrandGoogle size={16} />}
							onClick={() => setShowAuthModal(true)}
							loading={cloudLoading}
						>
							Sign in with Google
						</Button>
					)}
				</Stack>
			</Paper>

			{/* Local Backup Section */}

			<Stack gap='md'>
				<Backup bills={bills} onRestore={onRestoreBills} />
			</Stack>

			{/* Display Options Section */}
			<Paper withBorder p='md' radius='md'>
				<Stack gap='md'>
					<Text size='sm' fw={500}>
						Display Options
					</Text>
					<Stack gap='xs'>
						<Switch
							label='Show paid/remaining percentages'
							checked={showPercentages}
							onChange={handlePercentagesToggle}
						/>
						<Switch
							label='Dark mode'
							checked={colorScheme === 'dark'}
							onChange={handleThemeToggle}
						/>
					</Stack>
				</Stack>
			</Paper>

			{/* App Info Section */}
			<Paper withBorder p='md' radius='md'>
				<Stack gap='md'>
					<Group justify='space-between' align='center'>
						<Text size='sm' fw={500}>
							App Info
						</Text>
						<Tooltip
							label='Check for and install the latest app version'
							position='top'
							withArrow
						>
							<IconInfoCircle
								size={18}
								style={{ cursor: 'pointer' }}
							/>
						</Tooltip>
					</Group>
					<Group justify='space-between' align='center'>
						<Text size='sm' c='dimmed'>
							Version: {appVersion}
						</Text>
						<Button
							leftSection={<IconRefresh size={16} />}
							onClick={onCheckForUpdates}
							loading={isCheckingUpdates}
							variant='light'
							size='sm'
						>
							Check for Updates
						</Button>
					</Group>
				</Stack>
			</Paper>

			{/* Auth Modal */}
			<AuthModal
				opened={showAuthModal}
				onClose={() => setShowAuthModal(false)}
				onSignIn={handleSignInClick}
				loading={cloudLoading}
			/>
		</Stack>
	);
}

export default Settings;
